/**
 * Kleine, pure helpers om een gevangen fout of bron-URL veilig als telemetrie-
 * property mee te sturen (trackClientEvent / trackClientException).
 *
 * Aanleiding: de portal-documentviewer slikte fouten van het deelvenster
 * (navigator.share) en van de blob-fetch stil in en toonde alleen een rode balk
 * ("Opslaan mislukt"). Op een Samsung-toestel bleek daardoor wekenlang
 * onzichtbaar waarom een loonstrook niet te openen was. Deze helpers maken de
 * reden zichtbaar in App Insights zonder vrije tekst uit de app te lekken.
 *
 * Let op: custom-event-/exception-PROPERTIES passeren `redactTelemetryItem`
 * niet (die redigeert alleen de URL-velden en exception-messages van de SDK).
 * Daarom redigeren deze helpers zelf de token-dragende segmenten
 * (`redactTokenSegments`), maar laten ze cuids staan: een documentId in een
 * proxy-pad is bewust herleidbaar (opaak, geen persoonsgegeven).
 *
 *  - `foutVoorTelemetrie` geeft alleen de fout-NAAM (bv. `NotAllowedError`,
 *    `TypeError`) en het door de browser gegenereerde bericht, tokens
 *    geredigeerd en geklemd op 200 tekens. Geen stack, geen app-data.
 *  - `padVoorTelemetrie` reduceert een bron-URL tot het pad: geen host, geen
 *    query of hash (daar kunnen tokens in staan). Niet-http-schema's (`blob:`,
 *    `data:`) dragen hun origin of inhoud in het "pad" en worden tot het schema
 *    gereduceerd.
 */

import { redactTokenSegments } from './telemetry-redaction'

const MAX_LENGTE = 200

/** Eerst redigeren, dán klemmen: een token op de knipgrens mag niet half ontsnappen. */
function veilig(tekst: string): string {
  const geredigeerd = redactTokenSegments(tekst)
  return geredigeerd.length > MAX_LENGTE ? geredigeerd.slice(0, MAX_LENGTE) : geredigeerd
}

export interface FoutVoorTelemetrie {
  /** Fout-klasse zoals de browser 'm geeft; 'Onbekend' voor niet-Error-waarden. */
  foutNaam: string
  /** Browser-/runtime-bericht, tokens geredigeerd, geklemd op 200 tekens. */
  foutBericht: string
}

/** Beschrijf een gevangen waarde (Error, DOMException of iets anders) telemetrie-veilig. */
export function foutVoorTelemetrie(err: unknown): FoutVoorTelemetrie {
  if (err instanceof Error) {
    return { foutNaam: err.name || 'Error', foutBericht: veilig(err.message ?? '') }
  }
  if (err == null) return { foutNaam: 'Onbekend', foutBericht: '' }
  try {
    return { foutNaam: 'Onbekend', foutBericht: veilig(String(err)) }
  } catch {
    // Bv. Object.create(null): niet naar string te converteren. Nooit gooien.
    return { foutNaam: 'Onbekend', foutBericht: '' }
  }
}

/**
 * Alleen het pad van een (relatieve of absolute) URL: zonder host, query en
 * hash, tokens geredigeerd, geklemd. Niet-http(s)-URL's geven alleen hun schema
 * terug; een onparseerbare waarde een lege string (liever niets dan een lek).
 */
export function padVoorTelemetrie(url: string): string {
  if (!url) return ''
  try {
    // Relatieve paden resolven tegen een dummy-origin; alleen het pad blijft over.
    const u = new URL(url, 'http://telemetrie.invalid')
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return u.protocol
    return veilig(u.pathname)
  } catch {
    return ''
  }
}
